import { useState } from 'react';
import { forwardRef } from '@heroui/system';
import { Input, InputProps } from '@heroui/react';
import { IoCloseSharp } from 'react-icons/io5';
import { MdOutlineClose, MdOutlineSearch } from 'react-icons/md';
import IconButton from '../IconButton';
import { IconButtonProps } from '../IconButton/IconButton';

const customClearButton = (
  <span className="clear-button">
    <MdOutlineClose className="fill-base-20" />
  </span>
);

type CustomExtendableSearchInputProps = Omit<InputProps, 'ref'> & {
  buttonProps?: IconButtonProps;
  onClear?: () => void;
};

const SearchInput = forwardRef<'input', CustomExtendableSearchInputProps>((props, ref) => {
  const [inputVisibility, setInputVisibility] = useState(false);

  function handleInputVisibility() {
    setInputVisibility(!inputVisibility);
  }

  const { variant, buttonProps, isClearable, startContent, label, onClear, ...restProps } = props;

  const inputProps = restProps as Omit<
    InputProps,
    'ref' | 'variant' | 'buttonProps' | 'isClearable' | 'startContent' | 'label'
  >;

  return (
    <div className="flex items-start justify-end gap-1 w-full">
      <div
        className={`
          transition-[width,max-width] duration-300 ease-in-out overflow-hidden
          ${inputVisibility ? 'w-full max-w-full' : 'w-0 max-w-0'}
        `}
      >
        <Input
          ref={ref}
          startContent={<MdOutlineSearch size={22} className="fill-current tmdx-input__start-icon" />}
          endContent={isClearable ? customClearButton : undefined}
          isClearable={isClearable}
          radius="full"
          classNames={{
            base: 'tmdx-input tmdx-input-search group',
            inputWrapper: [
              'bg-transparent',
              'group-data[focus=true]:bg-base-50',
              'group-data-[invalid=true]:bg-red-80',
              'tmdx-input__wrapper',
              'transition-none!',
              startContent ? 'hasStartContent' : '',
              !label && 'min-h-7.5 h-7.5',
              !!label && 'hasLabel',
              'shadow-none',
              'border',
              'border-solid',
              'border-base-30',
            ],
            input: [
              'text-base-5',
              'placeholder:text-base-20',
              'text-m-m!',
              'group-data-[invalid=true]:text-base-5!',
              'tmdx-input__item',
            ],
            label: [
              'text-base-20',
              'text-s-r!',
              'tmdx-input__label',
              'group-focus-within:text-base-10',
              'group-data-[invalid=true]:text-danger!',
            ],
            helperWrapper: 'empty:hidden!',
            description: [
              'text-base-20',
              'text-s-r',
              'group-data-[invalid=true]:text-danger',
              'form-item__description',
            ],
            errorMessage: ['text-s-r', 'group-data-[invalid=true]:text-danger'],
            innerWrapper: ['bg-transparent', 'text-base-30', 'tmdx-input__inner-wrapper'],
            clearButton: ['opacity-100!'],
          }}
          {...inputProps}
        />
      </div>
      <IconButton
        className="shrink-0 translate-y-0.75"
        variant="solid"
        size="xs"
        onClick={handleInputVisibility}
        startContent={!inputVisibility ? <MdOutlineSearch size={16} /> : <IoCloseSharp onClick={onClear} size={16} />}
        {...buttonProps}
      />
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;
